// Utils
import ModelMapperApi from '@utils/modelMapperApi.compose'
import LoadingPromiseUtil from '@utils/loadingPromise'

// Services
import { PricingPlanService } from '@services/pricing/index'
import { ShippingOptionsService } from '@services/pricing/shippingOptions'
import { DestinationCountriesService } from '@services/pricing/destinationCountries'
import { OriginCountriesService } from '@services/pricing/originCountries'
import { PackageSelectionService } from '@services/pricing/packageSelection'
import { NetworksService } from '@/services/pricing/networks'
import { LocationsService } from '@/services/pricing/locations'

// Mappers
import PricingPlanMapper, { OUNCES } from '@/mappers/pricing/pricing'
import PricingZonesMapper from '@/mappers/pricing/zones'
import ShippingOptionsMapper from '@/mappers/pricing/shippingOptions'
import DestinationCountriesMapper from '@/mappers/pricing/destinationCountries'
import OriginCountriesMapper from '@/mappers/pricing/originCountries'
import PackageSelectionMapper from '@/mappers/pricing/packageSelection'

// Models
import PricingPlan from '@/models/pricing'
import PricingZone from '@/models/pricing/zones'
import ShippingOptions from '@/models/pricing/shippingOptions'
import DestinationCountries from '@/models/pricing/destinationCountries'
import { OriginCountries } from '@/models/pricing/originCountries'
import { PackageSelection } from '@/models/pricing/packageSelection'
import Networks from '@/models/pricing/networks'
import DefaultRates from '@/models/pricing/defaultRates'
import MerchantCountryCurrency from '@/models/pricing/merchantCountryCurrency'
import { AllCountryCurrency } from '@/models/pricing/countryCurrency'

const DIMENSIONAL_WEIGHT_CUTOFF = 16
const DEFAULT_CURRENCY = 'USD'
const US_ISOCODE = 'US'

export default {
  namespaced: true,
  state: {
    pricingPlan: new PricingPlan(),
    shippingOptions: new ShippingOptions(),
    destinationCountries: new DestinationCountries(),
    originCountries: new OriginCountries(),
    networks: new Networks(),
    shipBobNetworks: [],
    filteredNetworks: [],
    selectedDestinationCountries: [],
    selectedNetwork: null,
    selectedOriginCountry: null,
    selectedShippingOption: null,
    loadingPromiseUtil: new LoadingPromiseUtil(),
    unitOfMeasure: OUNCES,
    unitOfMeasureForm: OUNCES,
    refreshPricingPlan: true,
    pricingUpdated: false,
    showNetworkOption: false,
    zones: [],
    hasOriginCountryChanged: false,
    hasNetworkChanged: false,
    billableWeightEstimate: new PackageSelection(),
    allCountryCurrency: new AllCountryCurrency(),
    preservedOriginCountry: null,
    preservedOriginFC: null,
    defaultRates: new DefaultRates(),
    userHomeCountry: new MerchantCountryCurrency(),
    userCountryCurrencyMappings: [],
    selectedCountryCurrency: ''
  },
  mutations: {
    setPricingPlan (state, pricingPlan) {
      state.pricingPlan = pricingPlan
    },
    setShippingOptions (state, shippingOptions) {
      state.shippingOptions = shippingOptions
    },
    setSelectedShippingOption (state, shippingOption) {
      state.selectedShippingOption = shippingOption
    },
    setDestinationCountries (state, destinationCountries) {
      state.destinationCountries = destinationCountries
    },
    setSelectedDestinationCountries (state, destinationCountries) {
      state.selectedDestinationCountries = destinationCountries
    },
    setSelectedOriginCountry (state, originCountry) {
      state.selectedOriginCountry = originCountry
    },
    setNetworks (state, networks) {
      state.networks = networks
    },
    setShipBobNetworks (state, networks) {
      state.shipBobNetworks = networks
    },
    setFilteredNetworks (state, networks) {
      state.filteredNetworks = networks
    },
    setSelectedNetwork (state, network) {
      state.selectedNetwork = network
    },
    setUnitOfMeasure (state, unitOfMeasure) {
      state.unitOfMeasure = unitOfMeasure
    },
    setUnitOfMeasureForm (state, unitOfMeasure) {
      state.unitOfMeasureForm = unitOfMeasure
    },
    setRefreshPricingPlan (state, refreshPricingPlan) {
      state.refreshPricingPlan = refreshPricingPlan
    },
    setZones (state, zones) {
      state.zones = zones
    },
    setOriginCountries (state, originCountries) {
      state.originCountries = originCountries
    },
    setPricingUpdated (state, pricingUpdated) {
      state.pricingUpdated = pricingUpdated
    },
    setHasOriginCountryChanged (state, hasOriginCountryChanged) {
      state.hasOriginCountryChanged = hasOriginCountryChanged
    },
    setHasNetworkChanged (state, hasNetworkChanged) {
      state.hasNetworkChanged = hasNetworkChanged
    },
    setBillableWeightEstimate (state, billableWeightEstimate) {
      state.billableWeightEstimate = billableWeightEstimate
    },
    setAllCountryCurrencies (state, allCountryCurrency) {
      state.allCountryCurrency = allCountryCurrency
    },
    setPreservedOriginCountry (state, preservedOriginCountry) {
      state.preservedOriginCountry = preservedOriginCountry
    },
    setPreservedOriginFC (state, preservedOriginFC) {
      state.preservedOriginFC = preservedOriginFC
    },
    setDefaultRates (state, defaultRates) {
      state.defaultRates = defaultRates
    },
    setUserHomeCountry (state, userHomeCountry) {
      state.userHomeCountry = userHomeCountry
    },
    setUserCountryCurrencyMappings (state, userCountryCurrencyMappings) {
      state.userCountryCurrencyMappings = userCountryCurrencyMappings
    },
    setSelectedCountryCurrency (state, currencyCode) {
      state.selectedCountryCurrency = currencyCode
    }
  },
  actions: {
    async setUpPricing ({ state, dispatch }) {
      const promises = [dispatch('getShippingOptions'), dispatch('getOriginCountries')]
      if (this.getters.enablePricingPlansByPricingRegion) {
        state.showNetworkOption = true
        promises.push(dispatch('getNetworks'))
        promises.push(dispatch('getUserCountryCurrencyMappings'))
      }
      state.loadingPromiseUtil.addMany(promises, ['inputs'])
      Promise.all(promises).then(_val => {
        state.loadingPromiseUtil.add(dispatch('getPricingPlan'), ['table'])
      })
    },
    async getDefaultRates ({ commit }, { countryId, currencyCode }) {
      const response = await PricingPlanService.getDefaultRatesByHomeCountry(countryId, currencyCode)
      commit('setDefaultRates', response)
    },
    async getUserHomeCountry ({ commit }) {
      const homeCountryCurrency = await PricingPlanService.getUserHomeCountry()
      if (homeCountryCurrency) { commit('setUserHomeCountry', homeCountryCurrency) }
    },
    async getPricingPlan ({ commit, state, dispatch }) {
      if (!state.refreshPricingPlan) {
        commit('setUnitOfMeasure', state.unitOfMeasureForm)
        return
      }

      commit('setPreservedOriginCountry', state.selectedOriginCountry?.countryISO)
      commit('setPreservedOriginFC', state.selectedOriginCountry?.id)
      if (state.showNetworkOption) {
        state.loadingPromiseUtil.add(dispatch('getPricingPlanByPricingRegion'), ['table'])
      } else {
        state.loadingPromiseUtil.add(dispatch('getPricingPlanBySourceDestination'), ['table'])
      }
    },
    async getPricingPlanBySourceDestination ({ state, commit }) {
      let pricingZone = new PricingZone()
      let pricingPlan = new PricingPlan()

      const getZonesFunc = ModelMapperApi.get(
        ModelMapperApi.model(PricingZone),
        ModelMapperApi.mapper(PricingZonesMapper.mapZonesFromApi),
        ModelMapperApi.service(PricingPlanService.getPricingZones, PricingPlanService)
      )

      const getPricingPlanFunc = ModelMapperApi.get(
        ModelMapperApi.model(PricingPlan),
        ModelMapperApi.mapper(PricingPlanMapper.mapPricingPlanFromAPI),
        ModelMapperApi.service(PricingPlanService.getPricingPlan, PricingPlanService)
      )

      try {
        const fulfillmentCenterId = state.selectedOriginCountry.id
        const destinationCountryIsoCodes = state.selectedDestinationCountries.map(c => c.isoCode)
        const zonesRequest = { fulfillmentCenterId, destinationCountryIsoCodes }
        pricingZone = await getZonesFunc(zonesRequest)

        const zoneIds = pricingZone.zones.map(zone => zone.id)
        const shipOptionId = state.selectedShippingOption.id
        const pricingPlanRequest = { shipOptionId, zoneIds }
        pricingPlan = await getPricingPlanFunc(pricingPlanRequest)
        pricingPlan.destinations = state.selectedDestinationCountries
      } catch (ex) { }

      commit('setZones', pricingZone.zones)
      commit('setPricingPlan', pricingPlan)
      commit('setUnitOfMeasure', state.unitOfMeasureForm)
      commit('setRefreshPricingPlan', false)
      commit('setHasOriginCountryChanged', false)
      commit('setHasNetworkChanged', false)
    },
    async getPricingPlanByPricingRegion ({ state, commit }) {
      let pricingZone = new PricingZone()
      let pricingPlan = new PricingPlan()

      const getZonesWithCountryFunc = ModelMapperApi.get(
        ModelMapperApi.model(PricingZone),
        ModelMapperApi.mapper(PricingZonesMapper.mapZonesAndCountriesFromApi),
        ModelMapperApi.service(PricingPlanService.getPricingZones, PricingPlanService)
      )

      const getPricingRatesFunc = ModelMapperApi.get(
        ModelMapperApi.model(PricingPlan),
        ModelMapperApi.mapper(PricingPlanMapper.mapPricingPlanFromAPI),
        ModelMapperApi.service(PricingPlanService.getPricingRates, PricingPlanService)
      )

      try {
        // get is cross border flag from iscrossbordershipment for all source-destinations mapping
        const sourceIsoCode = state.selectedOriginCountry.countryISO
        const isCrossBorderShipmentRequest = []
        for (let i = 0; i < state.selectedDestinationCountries.length; i++) {
          const request = { requestId: (i + 1), sourceAddress: { countryISO: sourceIsoCode }, destinationAddress: { countryISO: state.selectedDestinationCountries[i].isoCode } }
          isCrossBorderShipmentRequest.push(request)
        }
        const isCrossBorderShipmentBulkResponse = await PricingPlanService.getIsCrossBorderShipmentBulk(isCrossBorderShipmentRequest)

        // get shipbob zone
        const fulfillmentCenterId = state.selectedNetwork ? state.selectedNetwork.id : state.selectedOriginCountry.id
        const destinationCountryIsoCodes = state.selectedDestinationCountries.map(c => c.isoCode)
        const zonesRequest = { fulfillmentCenterId, destinationCountryIsoCodes }
        pricingZone = await getZonesWithCountryFunc(zonesRequest)

        // estimate rate - shipoptionid, domestic zone plan, international zone plan
        const domesticZoneIds = []
        const internationalZoneIds = []
        for (let i = 0; i < state.selectedDestinationCountries.length; i++) {
          const isCrossBorder = isCrossBorderShipmentBulkResponse.find(cbResponse => cbResponse.requestId === (i + 1).toString()).isCrossBorder
          const zoneDetails = pricingZone.zones.filter(zone => zone.country === state.selectedDestinationCountries[i].name)

          if (isCrossBorder) {
            internationalZoneIds.push(...zoneDetails.map(zone => zone.id))
          } else if (sourceIsoCode === US_ISOCODE && state.selectedDestinationCountries[i].isoCode === US_ISOCODE) {
            internationalZoneIds.push(...zoneDetails.filter(zone => zone.id > 10).map(zone => zone.id))
            domesticZoneIds.push(...zoneDetails.filter(zone => zone.id <= 10).map(zone => zone.id))
          } else {
            domesticZoneIds.push(...zoneDetails.map(zone => zone.id))
          }
        }
        let domesticPlanID, internationalPlanID
        if (state.selectedNetwork) {
          domesticPlanID = state.selectedNetwork.domesticPlanID
          internationalPlanID = state.selectedNetwork.internationalPlanID
        } else {
          const sfnInfo = getShipbobNetworkInfo(state, commit, sourceIsoCode)
          domesticPlanID = sfnInfo?.domesticPlanID
          internationalPlanID = sfnInfo?.internationalPlanID
        }
        const estimateRateRequest = {
          shipOptionId: state.selectedShippingOption.id,
          domesticZonePlan: {
            planId: domesticPlanID,
            zoneIds: domesticZoneIds
          },
          internationalZonePlan: {
            planId: internationalPlanID,
            zoneIds: internationalZoneIds
          }
        }
        pricingPlan = await getPricingRatesFunc(estimateRateRequest)
        pricingPlan.destinations = state.selectedDestinationCountries
      } catch (ex) { }

      commit('setZones', pricingZone.zones)
      commit('setPricingPlan', pricingPlan)
      commit('setUnitOfMeasure', state.unitOfMeasureForm)
      commit('setRefreshPricingPlan', false)
      commit('setHasOriginCountryChanged', false)
      commit('setHasNetworkChanged', false)
    },
    async getExplicitPricingPlan ({ commit, state, dispatch }, { shipOptionId, zoneIds }) {
      let pricingPlan = new PricingPlan()

      const getPricingPlanFunc = ModelMapperApi.get(
        ModelMapperApi.model(PricingPlan),
        ModelMapperApi.mapper(PricingPlanMapper.mapPricingPlanFromAPI),
        ModelMapperApi.service(PricingPlanService.getPricingPlan, PricingPlanService)
      )

      const pricingPlanRequest = { shipOptionId, zoneIds }
      pricingPlan = await getPricingPlanFunc(pricingPlanRequest)
      commit('setPricingPlan', pricingPlan)
    },
    async getShippingOptions ({ commit, dispatch, state }, setInitialShippingOption = true) {
      let shippingOptions = []
      const getShippingOptionsFunc = ModelMapperApi.get(
        ModelMapperApi.model(ShippingOptions),
        ModelMapperApi.mapper(ShippingOptionsMapper.mapShippingOptionsFromApi),
        ModelMapperApi.service(ShippingOptionsService.getShippingOptions, ShippingOptionsService)
      )
      try {
        shippingOptions = await getShippingOptionsFunc()
      } catch (ex) {
      }
      commit('setShippingOptions', shippingOptions)
      if (setInitialShippingOption) commit('setSelectedShippingOption', shippingOptions.options[0] || null)
    },
    async getDestinationCountries ({ commit }, originLocation) {
      commit('setSelectedDestinationCountries', [])
      let getFunc = DestinationCountriesService.getDestinationCountries
      if (this.getters.enablePricingEnableAllActiveLocations) {
        getFunc = DestinationCountriesService.getActiveDestinationCountriesByFC
      }
      let destinationCountries = new DestinationCountries()
      const getDestinationCountriesFunc = ModelMapperApi.get(
        ModelMapperApi.model(DestinationCountries),
        ModelMapperApi.mapper(DestinationCountriesMapper.mapDestinationCountriesFromApi),
        ModelMapperApi.service(getFunc, DestinationCountriesService)
      )
      try {
        destinationCountries = await getDestinationCountriesFunc(originLocation)
      } catch (ex) { }
      commit('setDestinationCountries', destinationCountries)
    },
    async getNetworks ({ commit, dispatch, state }, originCountry) {
      const filteredNetworks = []
      if ((!state.networks || !state.networks.length) && state.networks instanceof Networks) {
        let networks = new Networks()
        let shipBobNetworks = []
        try {
          const userPlans = await NetworksService.getUserPlans()
          const merchantFCs = await NetworksService.getMerchantFCs()
          shipBobNetworks = userPlans.filter(plan => plan.pricingGroupId === 1)
          const merchantPlans = userPlans.filter(plan => plan.pricingGroupId === 2)
          networks = merchantPlans.filter(plan => { return merchantFCs.fulfillment_centers.find(fc => plan.fulfillmentCenters.includes(fc.fulfillment_center_id)) })
            .map(plan => {
              const fcs = merchantFCs.fulfillment_centers.filter(fc => plan.fulfillmentCenters.includes(fc.fulfillment_center_id))
              return {
                id: fcs[0]?.fulfillment_center_id,
                fulfillmentCenters: fcs,
                pricingRegionId: plan.pricingRegionId,
                pricingGroupId: plan.pricingGroupId,
                countryISO: plan.countryISO,
                domesticPlanID: plan.domesticPlanID,
                internationalPlanID: plan.internationalPlanID,
                domesticPlanCurrencyCode: plan.domesticPlanCurrencyCode,
                internationalPlanCurrencyCode: plan.internationalPlanCurrencyCode
              }
            })
        } catch (ex) { console.log(ex) }
        commit('setNetworks', networks)
        commit('setShipBobNetworks', shipBobNetworks)
      }
      if (originCountry && originCountry.countryISO) {
        dispatch('setSelectedNetwork', null)
        let groupIndex = 1
        const merchantNetworks = state.networks.filter(network => network.countryISO === originCountry.countryISO)
        if (merchantNetworks && merchantNetworks.length && originCountry.id) {
          const sfnInfo = getShipbobNetworkInfo(state, commit, originCountry.countryISO)
          const fcs = [{ fulfillment_center_id: originCountry.id, fulfillment_center_name: 'ShipBob Managed Fulfillment Centers' }]
          if (sfnInfo) {
            filteredNetworks.unshift({ id: originCountry.id, fulfillmentCenters: fcs, pricingGroupId: sfnInfo.pricingGroupId, pricingRegionId: sfnInfo.pricingRegionId, name: `Group ${groupIndex}`, domesticPlanID: sfnInfo.domesticPlanID, internationalPlanID: sfnInfo.internationalPlanID, domesticPlanCurrencyCode: sfnInfo.domesticPlanCurrencyCode, internationalPlanCurrencyCode: sfnInfo.internationalPlanCurrencyCode })
            groupIndex += 1
          }
        }

        merchantNetworks.forEach((merchantNetwork) => {
          filteredNetworks.push({ ...merchantNetwork, name: `Group ${groupIndex}` })
          groupIndex += 1
        })

        commit('setFilteredNetworks', filteredNetworks)
        if (!filteredNetworks || filteredNetworks.length === 0) { // If no M+ FCs, fetch destination based on source country FC id, else wait for network to be selected.
          dispatch('getDestinationCountries', originCountry.id)
        }
      }
    },
    async getOriginCountries ({ commit, dispatch }) {
      let getFunc = OriginCountriesService.getOriginCountries
      if (this.getters.enablePricingEnableAllActiveLocations) {
        getFunc = OriginCountriesService.getActiveOriginCountries
      }
      let originCountries = new OriginCountries()
      const getOriginCountriesFunc = ModelMapperApi.get(
        ModelMapperApi.model(OriginCountries),
        ModelMapperApi.mapper(OriginCountriesMapper.mapOriginCountriesFromApi),
        ModelMapperApi.service(getFunc, OriginCountriesService)
      )
      try {
        originCountries = await getOriginCountriesFunc()
      } catch (ex) {
      }
      commit('setOriginCountries', originCountries)
    },
    async calculatePackageSize ({ commit, dispatch }, formData) {
      let billableWeightEstimate = new PackageSelection()
      const getPackageSelectionFunc = ModelMapperApi.get(
        ModelMapperApi.model(PackageSelection),
        ModelMapperApi.mapper(PackageSelectionMapper.mapPackageSelectionFromApi),
        ModelMapperApi.post(
          ModelMapperApi.service(PackageSelectionService.calculatePackageSize, PackageSelectionService),
          ModelMapperApi.mapper(PackageSelectionMapper.mapPackageSelectiontoApi)
        )
      )

      try {
        billableWeightEstimate = await getPackageSelectionFunc(formData)

        let billableWeight, dimensionalWeight
        const packageSize = billableWeightEstimate.shipBobBox
        if (formData.orderTypeId === 1) {
          billableWeight = packageSize.domesticBillableWeight
          dimensionalWeight = packageSize.domesticDimensionalWeight || packageSize.domesticBillableWeight
        } else if (formData.orderTypeId === 2) {
          billableWeight = packageSize.internationalBillableWeight
          dimensionalWeight = packageSize.internationalDimensionalWeight || packageSize.internationalBillableWeight
        }

        packageSize.totalWeight = Math.ceil(packageSize.totalWeight)
        packageSize.dimensionalWeight = Math.ceil(dimensionalWeight)
        packageSize.billableWeight = Math.ceil(billableWeight)
      } catch (err) {
        console.error(err)
      }
      commit('setBillableWeightEstimate', billableWeightEstimate.shipBobBox)
    },
    async getAllCountryCurrency ({ commit }) {
      const allCountryCurrency = await LocationsService.getAllCountryCurrency()

      if (allCountryCurrency) { commit('setAllCountryCurrencies', allCountryCurrency) }
    },
    async getUserCountryCurrencyMappings ({ commit }) {
      const userCountryCurrencyMappings = await PricingPlanService.getUserCountryCurrencyMappings()
      if (userCountryCurrencyMappings) {
        commit('setUserCountryCurrencyMappings', userCountryCurrencyMappings)
      }
    },
    setSelectedShippingOption ({ commit }, shippingOption) {
      commit('setSelectedShippingOption', shippingOption)
      commit('setRefreshPricingPlan', true)
    },
    setSelectedDestinationCountries ({ commit }, destinationCountries) {
      commit('setSelectedDestinationCountries', destinationCountries)
      commit('setRefreshPricingPlan', true)
    },
    setSelectedOriginCountry ({ state, commit, dispatch }, originCountry) {
      commit('setHasOriginCountryChanged', originCountry !== state.selectedOriginCountry)
      commit('setSelectedOriginCountry', originCountry)
      if (state.showNetworkOption) { // If this setting is ON, destination will be fetched in the getNetworks
        dispatch('getNetworks', originCountry)
        commit('setHasNetworkChanged', (state.filteredNetworks.length === 0))
      } else {
        dispatch('getDestinationCountries', originCountry.id)
      }
    },
    setSelectedNetwork ({ state, commit, dispatch }, network) {
      commit('setHasNetworkChanged', (state.filteredNetworks.length === 0 || (network != null && (state.selectedNetwork == null || network.id !== state.selectedNetwork.id))))
      commit('setSelectedNetwork', network)
      if (network && network.id) { // Fetch destination countries on network change by FC Id
        dispatch('getDestinationCountries', network.id)
      }
      commit('setRefreshPricingPlan', true)
    },
    addLoading ({ state }, { promise, scopes }) {
      state.loadingPromiseUtil.add(promise, scopes)
    },
    setUnitOfMeasureForm ({ commit }, unitOfMeasure) {
      commit('setUnitOfMeasureForm', unitOfMeasure)
    },
    setPricingUpdated ({ commit }) {
      commit('setPricingUpdated', true)
    },
    setDestinationCountries ({ commit }) {
      commit('setDestinationCountries', new DestinationCountries())
    }
  },
  getters: {
    shippingOptions: state => state.shippingOptions.options,
    selectedShippingOption: state => state.selectedShippingOption,
    destinationCountries: state => state.destinationCountries.getCountries(),
    selectedDestinationCountries: state => state.selectedDestinationCountries,
    loading: state => scope => state.loadingPromiseUtil.isLoading(scope),
    pricingPlan: (state, getters) => {
      const pricingRatesWithUpdatedWeight = state.pricingPlan.pricingRates.map(pi => (
        {
          ...pi,
          weightRange: pi.weightRange.map(w => getters.unitOfMeasureMapper(w)),
          weightRangeText: pi.weightRange.map(w => getters.unitOfMeasureTextMapper(w))
        }
      ))
      return { ...state.pricingPlan, pricingRates: pricingRatesWithUpdatedWeight }
    },
    isDataAvailable: state => !!state.pricingPlan.pricingRates && state.pricingPlan.pricingRates.length > 0,
    dimensionalWeightCutOff: (state, getters) => getters.unitOfMeasureMapper(DIMENSIONAL_WEIGHT_CUTOFF),
    unitOfMeasure: state => state.unitOfMeasure,
    unitOfMeasureMapper: state => PricingPlanMapper.weightMappers[state.unitOfMeasure.Name],
    unitOfMeasureTextMapper: state => PricingPlanMapper.weightMappersText[state.unitOfMeasure.Name],
    unitOfMeasureForm: state => state.unitOfMeasureForm,
    destinationTypes: state => state.destinationCountries.getDestinationTypes(),
    pricingUpdated: state => state.pricingUpdated,
    zones: state => state.zones,
    originCountries: state => state.originCountries,
    selectedOriginCountry: state => state.selectedOriginCountry,
    networks: state => state.networks,
    filteredNetworks: state => state.filteredNetworks,
    selectedNetwork: state => state.selectedNetwork,
    showNetworkOption: state => state.showNetworkOption,
    hasOriginCountryChanged: state => state.hasOriginCountryChanged,
    hasNetworkChanged: state => state.hasNetworkChanged,
    billableWeightEstimate: state => state.billableWeightEstimate,
    preservedOriginCountry: state => state.preservedOriginCountry,
    preservedOriginFC: state => state.preservedOriginFC,
    defaultRates: state => state.defaultRates,
    userHomeCountry: state => state.userHomeCountry,
    allCountryCurrency: state => state.allCountryCurrency,
    userCountryCurrencyMappings: state => state.userCountryCurrencyMappings,
    selectedCountryCurrency: state => state.selectedCountryCurrency
  }
}
function getShipbobNetworkInfo (state, commit, sourceIsoCode) {
  const countryCurrency = state.userCountryCurrencyMappings?.find(country => country.countryCode === sourceIsoCode)
  const currency = countryCurrency ? countryCurrency.currencyCode : DEFAULT_CURRENCY
  commit('setSelectedCountryCurrency', currency)
  const sfnInfo = state.shipBobNetworks.find(network => network.countryISO === sourceIsoCode &&
    (!network.domesticPlanCurrencyCode || network.domesticPlanCurrencyCode.toUpperCase() === currency.toUpperCase()) &&
    (!network.internationalPlanCurrencyCode || network.internationalPlanCurrencyCode.toUpperCase() === currency.toUpperCase()))
  return sfnInfo
};
